import { useQuery } from '@tanstack/react-query';
import { AnalysisData, ExtendedAttributeCode, TradingMessage } from 'pages/trading/model';
import { DEFAULT_PRO_FILTER_SET, Service, useDataService } from 'services/data.service';
import { DataRequest, useDataRequest } from 'hooks/useDataRequest';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { DataResult } from 'models/data';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { DateRange } from 'components/filter/input/DateRangePicker/types';
import { tradingFiltersToDataRequest } from 'pages/trading/util';
import { useProductDataService } from 'services/product-data.service';
import { useIntl } from 'react-intl';

export const useMessages = () => {
  const getMessages = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<TradingMessage[]>('messages');

  const requestData = useDataRequest();
  const { accountNumber, currentDataVersion, isAdminView, isSuperAdmin } = useContext(TenantConfigContext);

  return useQuery({
    queryKey: ['trading_messages', accountNumber, currentDataVersion, requestData, isAdminView],
    queryFn: async (): Promise<TradingMessage[]> => {
      const response = await getMessages('all', requestData);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      const result = response.data!.sort((a, b) => b.currentDataVersion.localeCompare(a.currentDataVersion));
      result.filter(tm => (isAdminView && isSuperAdmin) || !tm.hidden);
      return result;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useAnalysisData = <T>(dataId: number | undefined) => {
  const requestData = useDataRequest();
  const { accountNumber } = useContext(TenantConfigContext);
  const getAnalysisData = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<AnalysisData<T[]>>(
    `analysisdata`
  );

  return useQuery({
    retry: 2,
    queryKey: ['trading_data', dataId, accountNumber],
    // only run query once we have a data id
    // https://tanstack.com/query/v5/docs/framework/react/guides/dependent-queries
    enabled: !!dataId,
    queryFn: async (): Promise<AnalysisData<T[]>> => {
      const response = await getAnalysisData(dataId!.toString(), requestData);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data!;
    },
    staleTime: 0,
  });
};

export const useMessage = (messageId?: string) => {
  const requestData = useDataRequest();
  const { accountNumber } = useContext(TenantConfigContext);

  const getMessage = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<TradingMessage>('message');

  return useQuery({
    enabled: !!messageId,
    retry: 1,
    queryKey: ['trading_message', messageId, accountNumber],
    queryFn: async (): Promise<TradingMessage> => {
      const response = await getMessage(messageId!, requestData);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data!;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useTotals = (
  measureGroup: string,
  filters?: Record<ExtendedAttributeCode, FilterValue[]>,
  comparisonRange?: DateRange
) => {
  const { isAdminView } = useContext(TenantConfigContext);
  const requestData = filters ? tradingFiltersToDataRequest(filters, isAdminView, comparisonRange) : undefined;
  const getData = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<DataResult>('totals');

  return useQuery({
    retry: 1,
    enabled: !!requestData,
    queryKey: ['trading_totals', measureGroup, filters, comparisonRange],
    queryFn: async (): Promise<DataResult> => {
      const response = await getData(measureGroup, requestData!);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data!;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useTimeSeries = (measure: string, requestData?: DataRequest) => {
  const getData = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<DataResult>('time-series');

  return useQuery({
    enabled: !!requestData,
    queryKey: ['trading__time_series', measure, requestData],
    queryFn: async (): Promise<DataResult> => {
      const response = await getData(measure, requestData!);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data!;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export const useDrilldownData = (key: string, measureCode: string, enabled: boolean, requestData?: DataRequest) => {
  const getData = useDataService(Service.Trading, DEFAULT_PRO_FILTER_SET).get<DataResult>(`drilldown/${key}`);

  return useQuery({
    enabled: !!requestData && enabled,
    queryKey: ['drilldown', key, measureCode, requestData],
    queryFn: async (): Promise<DataResult> => {
      const response = await getData(measureCode, requestData!);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }
      return response.data!;
    },
    staleTime: 10 * 60 * 1000,
  });
};

export interface ProductNameAndUrl {
  name: string;
  url: string;
}

export const useProductNameAndUrl = (productNumber: string) => {
  const getData = useDataService(Service.Products, DEFAULT_PRO_FILTER_SET).get<ProductNameAndUrl>(`name-url`);
  const requestData = useDataRequest();
  const { accountNumber } = useContext(TenantConfigContext);

  const amendedRequestData = {
    ...requestData,
    filters: {
      ...requestData.filters,
      productNumbers: [productNumber],
    },
  };

  return useQuery({
    queryKey: ['productName', accountNumber, productNumber],
    queryFn: async (): Promise<ProductNameAndUrl> => {
      const response = await getData('', amendedRequestData);
      if (response.error) {
        // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
        // This assures useQuery sees this as an error and exposes it via it's standard mechanism
        throw Error(response.error.message);
      }

      return response.data!;
    },
    staleTime: 0,
  });
};

export const useTranslatedAttribute = (attributeCode: ExtendedAttributeCode, attributeValue: string) => {
  const { fetchProductDataResponse } = useProductDataService();
  const { accountNumber } = useContext(TenantConfigContext);
  const intl = useIntl();

  return useQuery({
    queryKey: ['translatedAttribute', accountNumber, attributeCode, attributeValue],
    queryFn: async (): Promise<string> => {
      async function getProductName(productNumber: string): Promise<string> {
        const response = await fetchProductDataResponse(productNumber);
        if (response.error) {
          // Needed as our 'makeApiCall' is not returning standard error response but just an error attribute
          // This assures useQuery sees this as an error and exposes it via it's standard mechanism
          throw Error(response.error.message);
        }
        if (response.data && response.data.list.length > 0) {
          return response.data.list[0].name;
        }

        return productNumber;
      }

      if (attributeCode === 'prdNumber') {
        return getProductName(attributeValue);
      }

      const translateAttributeValue = () => {
        switch (attributeCode) {
          case 'chnMarketplace':
          case 'prdBrand':
            return attributeValue;
          case 'prdCategory':
            return intl.formatMessage({
              id: `product.categories.${attributeValue}`,
              defaultMessage: attributeValue,
            });
          case 'chnCountry':
            return intl.formatDisplayName(attributeValue, { type: 'region' }) ?? attributeValue;
          default:
            return attributeValue;
        }
      };

      return translateAttributeValue();
    },
    staleTime: 0,
  });
};
